if (!customElements.get('coffee-calculator')) {
	class CoffeeCalculator extends HTMLElement {
		constructor() {
			super();

			this.BAG_12OZ = 340;
			this.BAG_2LB = 907;
			this.BAG_5LB = 2268;
			this.DAYS = Number(this.dataset.days) || 30;

			this.isPopup = this.hasAttribute('data-coffee-calculator-popup');

			this._onKeydown = this._onKeydown.bind(this);
			this._onBackdropClick = this._onBackdropClick.bind(this);
		}

		connectedCallback() {
			this.peopleInput = this.querySelector('[data-people]');
			this.peopleLabel = this.querySelector('[data-people-label]');
			this.cupsInput = this.querySelector('[data-cups]');
			this.gramsInput = this.querySelector('[data-grams]');
			this.minusButton = this.querySelector('[data-minus]');
			this.plusButton = this.querySelector('[data-plus]');
			this.calculateButton = this.querySelector('[data-calculate]');

			this.inner = this.querySelector('.coffee-calculator__inner');
			this.preresult = this.querySelector('.coffee-calculator__preresult');
			this.result = this.querySelector('.coffee-calculator__result');

			this.overlay = this.querySelector('[data-overlay]');
			this.closeButtons = this.querySelectorAll('[data-close]');
			this.inner = this.querySelector('.coffee-calculator__inner');

			this.preresult = this.querySelector('[data-preresult]');
			this.result = this.querySelector('[data-result]');
			this.total = this.querySelector('[data-total]');
			this.mixed = this.querySelector('[data-mixed]');
			this.twoLb = this.querySelector('[data-2lb]');
			this.twelveOz = this.querySelector('[data-12oz]');

			this.options = this.querySelector('.coffee-calculator__options');

			this._bindSelectWrappers();
			this._bindStepper();
			this._bindPeopleLabel();
			this._bindCalculate();
			this._bindClose();
			this._bindCta();
		}

		disconnectedCallback() {
			document.removeEventListener('keydown', this._onKeydown);
			this._unlockScroll();
		}

		_bindSelectWrappers() {
			this.querySelectorAll('.select-wrapper').forEach((wrap) => {
				const select = wrap.querySelector('select');
				if (!select) return;

				wrap.addEventListener('click', (e) => {
					if (e.target === select) return;

					select.focus();

					if (typeof select.showPicker === 'function') {
						try {
							select.showPicker();
						} catch (err) {
						}
					}
				});
			});
		}

		_bindStepper() {
			this.minusButton?.addEventListener('click', () => {
				const min = Number(this.gramsInput.min) || 10;
				const current = Number(this.gramsInput.value) || min;
				this.gramsInput.value = Math.max(min, current - 1);
			});

			this.plusButton?.addEventListener('click', () => {
				const max = Number(this.gramsInput.max) || 40;
				const current = Number(this.gramsInput.value) || 0;
				this.gramsInput.value = Math.min(max, current + 1);
			});
		}

		_bindPeopleLabel() {
			if (!this.peopleInput || !this.peopleLabel) return;
			const update = () => {
				this.peopleLabel.textContent =
					Number(this.peopleInput.value) === 1 ? 'Person' : 'People';
			};
			this.peopleInput.addEventListener('change', update);
			update();
		}

		_bindCalculate() {
			this.calculateButton?.addEventListener('click', () => this.calculate());
		}

		_bindClose() {
			this.closeButtons.forEach((btn) =>
				btn.addEventListener('click', () => this.close())
			);
			this.overlay?.addEventListener('click', () => this.close());
			if (this.isPopup) {
				this.addEventListener('click', this._onBackdropClick);
			}
			document.addEventListener('keydown', this._onKeydown);
		}

		_onBackdropClick(e) {
			const insideContent =
				this.inner?.contains(e.target) ||
				this.preresult?.contains(e.target) ||
				this.result?.contains(e.target);

			if (insideContent) return;

			this.close();
		}

		_bindCta() {
			if (!this.isPopup) return;
			document.querySelectorAll('[data-coffee-calculator-cta]').forEach((cta) => {
				cta.addEventListener('click', (e) => {
					e.preventDefault();
					this.openPopup();
				});
			});
		}

		_onKeydown(e) {
			if (e.key === 'Escape' && this.isOpen()) this.close();
		}

		/* ---------- scroll lock ---------- */

		_lockScroll() {
			document.documentElement.style.overflow = 'hidden';
			document.body.style.overflow = 'hidden';
		}

		_unlockScroll() {
			document.documentElement.style.overflow = '';
			document.body.style.overflow = '';
		}

		/* ---------- state ---------- */

		isOpen() {
			if (this.isPopup) return !this.classList.contains('hidden');
			return this.result ? !this.result.classList.contains('hidden') : false;
		}

		openPopup() {
			if (!this.isPopup) return;
			this.classList.remove('hidden');
			this._lockScroll();
		}

		openResult() {
			if (this.preresult) this.preresult.classList.add('hidden');
			if (this.result) this.result.classList.remove('hidden');
		}

		close() {
			if (this.result) this.result.classList.add('hidden');
			if (this.preresult) this.preresult.classList.remove('hidden');
			if (this.overlay) this.overlay.hidden = true;
			if (this.isPopup) this.classList.add('hidden');
			this._unlockScroll();
		}

		renderBag(count, label, size = '100') {
			if (!count) return '';

			return `
			<div class="coffee-calculator__option-item">
				<div class="coffee-calculator__option-icons">
					<div class="coffee-calculator__bag" style="--size:${size}%;">
						<img
							src="${this.dataset.bagIcon}"
							alt="${label}"
							sizes="20vw"
							decoding="async"
							class="coffee-calculator__bag-image"
						>
						<span class="coffee-calculator__bag-count">×${count}</span>
					</div>
				</div>

				<div class="coffee-calculator__option-label">
					<span class="coffee-calculator__option-amount">${count} ×</span>
					<span class="coffee-calculator__option-weight">${label}</span>
				</div>
			</div>
		`;
		}

		renderOption(bags) {
			if (!Array.isArray(bags) || bags.length === 0) return '';

			const sizeClass = (index) => {
				if (index === 0) return 'coffee-calculator__bag--big';
				if (index === 1) return 'coffee-calculator__bag--medium';
				return 'coffee-calculator__bag--small';
			};

			const plus = `
				<div class="coffee-calculator__option-plus">
					<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
						<path d="M3.90027 9.74001V5.86001H0.000273481V3.90001H3.90027V6.79493e-06H5.88027V3.90001H9.76027V5.86001H5.88027V9.74001H3.90027Z" fill="#C79A78"/>
					</svg>
				</div>
			`;

			const icons = bags.map((bag, index) => `
				<div class="coffee-calculator__bag ${sizeClass(index)}">
					<img
						src="${this.dataset.bagIcon}"
						alt="${bag.label}"
						class="coffee-calculator__bag-image"
					/>

					<span class="coffee-calculator__bag-count">
						x${bag.count}
					</span>
				</div>
				${index < bags.length - 1 ? plus : ''}
			`).join('');

			const rows = bags.map((bag, index) => `
				<div class="coffee-calculator__option-row">
					<span class="coffee-calculator__option-amount">
						${bag.count} ×
					</span>

					<span class="coffee-calculator__option-weight">
						${bag.label}
					</span>
				</div>

				${index < bags.length - 1 ? `<span class="coffee-calculator__option-plus mobile">+</span>` : ''}
			`).join('');

			return `
				<div class="coffee-calculator__option-item">
					<div class="coffee-calculator__option-icons">
						${icons}
					</div>

					<div class="coffee-calculator__option-list">
						${rows}
					</div>
				</div>
			`;
		}

		buildRecommendation(totalGrams, sizes, { allowSmallest = true } = {}) {
			let remaining = totalGrams;
			const result = [];

			sizes.forEach((bag, index) => {
				let count = 0;

				if (index === sizes.length - 1) {
					count = Math.ceil(remaining / bag.grams);
				} else {
					count = Math.floor(remaining / bag.grams);
					remaining -= count * bag.grams;
				}

				if (count > 0) {
					result.push({
						count,
						label: bag.label,
						size: bag.size
					});
				}
			});

			if (!result.length) {
				const smallest = sizes[sizes.length - 1];

				result.push({
					count: 1,
					label: smallest.label,
					size: smallest.size
				});
			}

			if (!allowSmallest && result.length > 1) {
				result.pop();
			}

			return result;
		}

		_bindCalculate() {
			this.calculateButton?.addEventListener('click', () => this.calculate());
		}

		_waitForImages(container) {
			const imgs = Array.from(container.querySelectorAll('img'));
			if (!imgs.length) return Promise.resolve();

			return Promise.all(
				imgs.map((img) => {
					if (img.complete) return Promise.resolve();

					return new Promise((resolve) => {
						img.addEventListener('load', resolve, { once: true });
						img.addEventListener('error', resolve, { once: true });
					});
				})
			);
		}

		_scrollToResult() {
			if (!this.result || window.innerWidth >= 990) return;

			if (this.isPopup) {
				const top = this.result.getBoundingClientRect().top - this.getBoundingClientRect().top + this.scrollTop	- 20;
				this.scrollTo({ top, behavior: 'smooth' });
			} else {
				const top = this.result.getBoundingClientRect().top + window.pageYOffset - 65;
				window.scrollTo({ top, behavior: 'smooth' });
			}
		}

		async calculate() {
			const people = Number(this.peopleInput.value);
			const cups = Number(this.cupsInput.value);
			const days = this.DAYS;
			const grams = Number(this.gramsInput.value);

			const totalGrams = people * cups * days * grams;

			const option1 = this.buildRecommendation(totalGrams, [
				{ grams: this.BAG_5LB, label: '5lb bag', size: 100 },
				{ grams: this.BAG_2LB, label: '2lb bag', size: 80 },
				{ grams: this.BAG_12OZ, label: '12oz bag', size: 65 }
			]);

			const has5lb = option1.some(
				(bag) => bag.label === '5lb bag' && bag.count >= 1
			);

			const option2 = this.buildRecommendation(totalGrams, [
				{ grams: this.BAG_2LB, label: '2lb bag', size: 100 },
				{ grams: this.BAG_12OZ, label: '12oz bag', size: 80 }
			]);

			const option3 = this.buildRecommendation(totalGrams, [
				{ grams: this.BAG_12OZ, label: '12oz bag', size: 80 }
			]);

			if (this.total) {
				this.total.textContent = `${totalGrams.toLocaleString()}g of coffee`;
			}

			if (this.mixed) {
				if (has5lb) {
					this.mixed.classList.remove('hidden');
					this.mixed.innerHTML = this.renderOption(option1);
				} else {
					this.mixed.classList.add('hidden');
					this.mixed.innerHTML = '';
				}
			}
			if (this.twoLb) this.twoLb.innerHTML = this.renderOption(option2);
			if (this.twelveOz) this.twelveOz.innerHTML = this.renderOption(option3);

			const items = [
				...(this.mixed?.querySelectorAll('.coffee-calculator__option-item') ?? []),
				...(this.twoLb?.querySelectorAll('.coffee-calculator__option-item') ?? []),
				...(this.twelveOz?.querySelectorAll('.coffee-calculator__option-item') ?? [])
			];

			items.forEach((item) => item.classList.add('is-loading'));

			if (this.options) {
				await this._waitForImages(this.options);
			}

			this.openResult();
			setTimeout(() => {
				this._scrollToResult();
			}, 0);

			requestAnimationFrame(() => {
				items.forEach((item) => item.classList.remove('is-loading'));
			});
		}
	}

	customElements.define('coffee-calculator', CoffeeCalculator);
}